import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { addMonths, addYears, subDays } from 'date-fns';
import type { Contract } from '../entities/Contract.js';
import type { ContractSupplement, ContractSupplementInnerSection } from '../entities/ContractSupplement.js';

/** an employment without a termination date is shown for exactly this long into the future */
export const OPEN_END_YEARS = 5;

export type AxisSupplement = NarrowedEntity<
  ContractSupplement,
  {
    startDateGeneral: 1;
    endDateGeneral: 1;
    startDateAG1: 1;
    endDateAG1: 1;
    startDateAG2: 1;
    endDateAG2: 1;
  }
>;

export type AxisContract = NarrowedEntity<
  Contract,
  { agInfos: { einstellungsdatum: 1; gekuendigtZum: 1 }; supplements: { startDateGeneral: 1 } }
>;

export interface SectionInterval {
  section: ContractSupplementInnerSection;
  start: Maybe<Date>;
  end: Maybe<Date>;
}

export const sectionIntervals = (supplement: AxisSupplement): SectionInterval[] => [
  { section: 'general', start: supplement.startDateGeneral, end: supplement.endDateGeneral },
  { section: 'ag1', start: supplement.startDateAG1, end: supplement.endDateAG1 },
  { section: 'ag2', start: supplement.startDateAG2, end: supplement.endDateAG2 },
];

const maxDate = (dates: Date[]) =>
  dates.reduce<Maybe<Date>>((latest, date) => (!latest || date > latest ? date : latest), null);

const minDate = (dates: Date[], fallback: Date) =>
  dates.reduce((earliest, date) => (date < earliest ? date : earliest), fallback);

/**
 * Breathing room before the first date on the axis, so a today marker sitting there does not land flush on
 * the start of the timeline.
 */
export const AXIS_LEAD_DAYS = 14;

/**
 * The earliest thing that happens on the contract, its start or the start of one of its supplements.
 *
 * A contract that only starts in the future would push the whole axis past today and drop the today marker
 * off it, so in that case the axis reaches back to shortly before today instead.
 */
export const resolveAxisStart = (
  contractStart: Date,
  supplements: AxisSupplement[],
  now: Date = todayAsCalendarDay(),
) => {
  const starts = supplements
    .flatMap(supplement => sectionIntervals(supplement).map(interval => interval.start))
    .filter((date): date is Date => !!date);

  const earliest = minDate(starts, contractStart);

  return earliest > now ? subDays(now, AXIS_LEAD_DAYS) : earliest;
};

/**
 * Breathing room after the last date on the axis, so the marker for it does not sit flush against the right
 * edge where it is only reachable by scrolling the very last pixel.
 */
export const AXIS_HEADROOM_MONTHS = 3;

/**
 * The right edge of the timeline.
 *
 * A contract without a termination date keeps running, so the axis always looks a fixed span ahead - the end
 * dates of its supplements must not cut it short. Once the contract itself has an end, the axis stops there,
 * but never before today, so a contract that lies in the past is still shown in relation to now.
 */
export const resolveAxisEnd = (
  options: { contractEnd: Maybe<Date>; supplements: AxisSupplement[] },
  axisStart: Date,
  now: Date = todayAsCalendarDay(),
) => {
  if (!options.contractEnd) return addYears(now, OPEN_END_YEARS);

  // a supplement may run past the end of the contract it belongs to
  const supplementEnds = options.supplements
    .flatMap(supplement => sectionIntervals(supplement).map(interval => interval.end))
    .filter((date): date is Date => !!date);

  const lastRelevantDate = maxDate([options.contractEnd, ...supplementEnds, now]) ?? now;

  // everything happened before today, so today is the last thing on the axis and needs room after it
  const latestEnd = lastRelevantDate <= now ? addMonths(now, AXIS_HEADROOM_MONTHS) : lastRelevantDate;

  // an end before the start would collapse the axis onto itself
  return latestEnd > axisStart ? latestEnd : addMonths(axisStart, 12);
};
